import { createAgentClient, createSessionApi } from "@recordsure/unity-agent";

import * as apiFactory from "./ApiFactory";
import { isApiError } from "./Api";

import { config } from "@/config";
import { authMachine } from "@/machines/auth-machine";

import type { AgentClient, UnityApiTransport } from "@recordsure/unity-agent";

// UnityApi errors are plain { status, statusText } objects; @recordsure/unity-agent shows Error messages.
function asError(e: unknown): unknown {
  return isApiError(e) ? new Error(`${e.status} ${e.statusText}`) : e;
}

const unityApi: UnityApiTransport = {
  async get<T>(path: string) {
    const client = await apiFactory.WithBearer();
    try {
      return await client.get<T>(path);
    } catch (e) {
      // The client throws a null result as a 204.
      if (isApiError(e) && e.status === 204) return null;
      throw asError(e);
    }
  },
  async post<T>(path: string, body?: unknown) {
    const client = await apiFactory.WithBearer();
    try {
      return await client.post<unknown, T>(path, body);
    } catch (e) {
      throw asError(e);
    }
  },
};

/** UnityApi's agent session endpoints, which a case binds its session to whether or not the agent is configured here. */
export const agentSession = createSessionApi(unityApi);

let agentClient: AgentClient | undefined;

/** The page's agent client — only for where config.agent is set, as the Agent Assistant panel checks. */
export function getAgentClient(): AgentClient {
  if (config.agent == null) {
    throw new Error("Agent Assistant is not configured for this environment (missing config.json 'agent' block).");
  }
  agentClient ??= createAgentClient({
    agent: config.agent,
    getLoginHint: () => authMachine.getSnapshot().context.user?.email,
    unityApi,
  });
  return agentClient;
}
